import { useCallback } from 'react';
import Konva from 'konva';
import { useTemplateStore } from './useTemplateStore';
import { Element } from '../../core/template.schemas';

/**
 * Hook for handling element transformations (drag, resize, rotate),
 * based on the official react-konva demo for performance and correctness.
 */
export const useElementTransform = (
  stage: Konva.Stage | null,
  snappingHandlers: {
    handleDragSnapping: (node: Konva.Node) => void;
    handleResizeSnapping: (node: Konva.Node) => void;
    clearSnapLines: () => void;
  },
) => {
  const updateElement = useTemplateStore((state) => state.updateElement);
  // Removed ephemeral updates for smoothness like shapes
  const { handleDragSnapping, handleResizeSnapping, clearSnapLines } = snappingHandlers;

  /**
   * Handles element drag movement.
   * Triggers drag snapping.
   */
  const handleElementDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      handleDragSnapping(e.target);
    },
    [handleDragSnapping],
  );

  /**
   * Handles the end of a drag operation.
   * Clears snapping guides and updates the element's position.
   */
  // Map dummy rect id back to element id
  const getElementId = (node: Konva.Node): string => {
    const id = node.id();
    return id.endsWith('-transform-target')
      ? id.slice(0, '-transform-target'.length * -1)
      : id;
  };

  const handleElementDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      clearSnapLines();
      const node = e.target;
      // Use local coordinates for all drag operations (group is the draggable target)
      updateElement(getElementId(node), {
        x: node.x() - (node.offsetX() || 0),
        y: node.y() - (node.offsetY() || 0),
      });
    },
    [clearSnapLines, updateElement],
  );

  /**
   * Handles live element transformation (resizing/rotating).
   * Triggers resize snapping.
   * No state updates are performed here to ensure high performance.
   */
  const handleElementTransform = useCallback(
    (e: Konva.KonvaEventObject<Event>) => {
      // Keep resize snapping active, but do not update store for performance
      handleResizeSnapping(e.target);
      // No ephemeral store updates here to avoid global re-renders
    },
    [handleResizeSnapping],
  );

  /**
   * Handles the end of a transformation.
   * Calculates the new properties of the element and updates the state.
   * This follows the logic from react-konva's official examples
   * to avoid compounding scales and ensure correct final dimensions.
   */
  const handleElementTransformEnd = useCallback(
    (e: Konva.KonvaEventObject<Event>) => {
      clearSnapLines();
      const node = e.target;
      if (!node) return;

      const stage = node.getStage();
      if (!stage) return;

      // Capture rect BEFORE resetting scale to get the visual bounding box (includes rotation & camera)
      const rectAbs = node.getClientRect({ skipStroke: true });
      const invStageTransform = stage.getAbsoluteTransform().copy().invert();
      const topLeft = invStageTransform.point({ x: rectAbs.x, y: rectAbs.y });

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // Reset scale to avoid compounding
      node.scaleX(1);
      node.scaleY(1);

      const newWidth = Math.max(5, node.width() * scaleX);
      const newHeight = Math.max(5, node.height() * scaleY);

      // Always use the axis-aligned top-left from the visual bounding box
      const newX = topLeft.x;
      const newY = topLeft.y;

      const updatePayload: Partial<Element> = {
        x: newX,
        y: newY,
        rotation: node.rotation(),
        width: newWidth,
        height: newHeight,
      };

      updateElement(
        node.id().endsWith('-transform-target')
          ? node.id().slice(0, '-transform-target'.length * -1)
          : node.id(),
        updatePayload
      );

      // For image dummy targets: reset rotation and re-center rect for next transform
      if (node.id().endsWith('-transform-target')) {
        node.rotation(0);
        node.position({ x: node.width() / 2, y: node.height() / 2 });
      }
    },
    [clearSnapLines, updateElement],
  );

  return {
    handleElementDragMove,
    handleElementDragEnd,
    handleElementTransform,
    handleElementTransformEnd,
  };
};